<template>
  <div>
    <div
        class="editor-container editor-container_classic-editor editor-container_include-style editor-container_include-word-count"
        ref="editorContainerElement"
    >
      <div>
        <div ref="editorElement">
          <Ckeditor
              v-if="editor && config"
              :editor="editor"
              :config="config"
              :model-value="modelValueSafe"
              @ready="onEditorReady"
              @update:model-value="onUpdateModelValue"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, onMounted, defineProps, defineEmits} from 'vue';
import { Ckeditor, useCKEditorCloud } from '@ckeditor/ckeditor5-vue';

const cloud = useCKEditorCloud( {
  version: '47.6.1',
  premium: false
} );

const editor = computed( () => {
  if ( !cloud.data.value ) {
    return null;
  }
  return cloud.data.value.CKEditor.ClassicEditor;
} );

const config = computed( () => {
  if ( !cloud.data.value ) return {};
  return {
    licenseKey: process.env.VUE_APP_CKEDITOR5_LICENSE_KEY,
    toolbar: {
      items: [
        'heading',
        'style',
        '|',
        'fontSize',
        'fontFamily',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'subscript',
        'superscript',
        'code',
        '|',
        'horizontalLine',
        'link',
        'insertImage',
        'insertImageViaUrl',
        'mediaEmbed',
        'insertTable',
        'highlight',
        'blockQuote',
        'codeBlock',
        '|',
        'alignment',
        '|',
        'bulletedList',
        'numberedList',
        'todoList',
        'outdent',
        'indent'
      ],
      shouldNotGroupWhenFull: true
    },
    plugins: [
      cloud.data.value.CKEditor.Alignment,
      cloud.data.value.CKEditor.Autoformat,
      cloud.data.value.CKEditor.AutoImage,
      cloud.data.value.CKEditor.AutoLink,
      cloud.data.value.CKEditor.Autosave,
      cloud.data.value.CKEditor.BalloonToolbar,
      cloud.data.value.CKEditor.BlockQuote,
      cloud.data.value.CKEditor.Bold,
      cloud.data.value.CKEditor.Code,
      cloud.data.value.CKEditor.CodeBlock,
      cloud.data.value.CKEditor.Essentials,
      cloud.data.value.CKEditor.FontBackgroundColor,
      cloud.data.value.CKEditor.FontColor,
      cloud.data.value.CKEditor.FontFamily,
      cloud.data.value.CKEditor.FontSize,
      cloud.data.value.CKEditor.GeneralHtmlSupport,
      cloud.data.value.CKEditor.Heading,
      cloud.data.value.CKEditor.Highlight,
      cloud.data.value.CKEditor.HorizontalLine,
      cloud.data.value.CKEditor.HtmlComment,
      cloud.data.value.CKEditor.ImageBlock,
      cloud.data.value.CKEditor.ImageCaption,
      cloud.data.value.CKEditor.ImageInline,
      cloud.data.value.CKEditor.ImageInsert,
      cloud.data.value.CKEditor.ImageInsertViaUrl,
      cloud.data.value.CKEditor.ImageStyle,
      cloud.data.value.CKEditor.ImageTextAlternative,
      cloud.data.value.CKEditor.ImageToolbar,
      cloud.data.value.CKEditor.ImageUpload,
      cloud.data.value.CKEditor.Indent,
      cloud.data.value.CKEditor.IndentBlock,
      cloud.data.value.CKEditor.Italic,
      cloud.data.value.CKEditor.Link,
      cloud.data.value.CKEditor.LinkImage,
      cloud.data.value.CKEditor.List,
      cloud.data.value.CKEditor.MediaEmbed,
      cloud.data.value.CKEditor.Paragraph,
      cloud.data.value.CKEditor.ShowBlocks,
      cloud.data.value.CKEditor.SourceEditing,
      cloud.data.value.CKEditor.Strikethrough,
      cloud.data.value.CKEditor.Style,
      cloud.data.value.CKEditor.Subscript,
      cloud.data.value.CKEditor.Superscript,
      cloud.data.value.CKEditor.Table,
      cloud.data.value.CKEditor.TableCaption,
      cloud.data.value.CKEditor.TableToolbar,
      cloud.data.value.CKEditor.TextPartLanguage,
      cloud.data.value.CKEditor.TextTransformation,
      cloud.data.value.CKEditor.TodoList,
      cloud.data.value.CKEditor.Underline
    ],
    fontFamily: { supportAllValues: true },
    fontSize: {
      options: [10, 12, 14, 'default', 18, 20, 22],
      supportAllValues: true
    },
    htmlSupport: {
      allow: [
        { name: /^.*$/, styles: true, attributes: true, classes: true }
      ]
    },
    initialData: modelValueSafe.value,
    language: 'pl',
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://'
    },
    list: {
      properties: { styles: true, startIndex: true, reversed: true }
    },
    menuBar: { isVisible: true },
    placeholder: ''
  };
});

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  }
})
const emit = defineEmits(['update:modelValue'])
const isLayoutReady = ref(false)
const editorInstance = ref(null)
const modelValueSafe = computed(() => props.modelValue ?? '')

onMounted(() => {
  isLayoutReady.value = true
})

function onEditorReady(editor) {
  editorInstance.value = editor
}

function onUpdateModelValue(value) {
  emit('update:modelValue', value)
}
</script>

<style>
.ck-editor__editable {
    min-height: 400px;
    max-height: 800px;
    width: auto;
    overflow-y: auto;
}

@media (max-width: 768px) {
    .ck-editor__editable {
        min-height: 150px;
    }

    .ck.ck-toolbar {
        flex-wrap: wrap;
    }

    .ck.ck-toolbar > .ck-toolbar__items {
        flex-wrap: wrap;
    }
}

.dark .ck.ck-menu-bar {
    background-color: #4b5563 !important;
    border-color: #4b5563 !important;
}

.dark .ck.ck-editor__main > .ck-editor__editable {
    background-color: #374151 !important;
    color: #e5e7eb !important;
    border: none;
}

.dark .ck.ck-editor__main > .ck-editor__editable:focus {
    border-color: #4b5563 !important;
}

.dark .ck.ck-toolbar {
    background-color: #1f2937 !important;
    border-color: #4b5563 !important;
}

.dark .ck.ck-menu-bar__menu.ck-menu-bar__menu_top-level > .ck-menu-bar__menu__button {
    background-color: #374151 !important;
}

.dark .ck.ck-button,
.dark .ck.ck-button.ck-on {
    color: #e5e7eb !important;
}

.dark .ck.ck-button:hover,
.dark .ck.ck-button.ck-on:hover {
    background-color: #4b5563 !important;
    cursor: pointer;
}

.dark .ck.ck-icon :not([fill]) {
    fill: currentColor !important;
}

.dark .ck.ck-dropdown__panel {
    background-color: #1f2937 !important;
    border-color: #4b5563 !important;
}

.dark .ck.ck-list__item {
    color: #e5e7eb !important;
}

.dark .ck.ck-list__item:hover:not(.ck-disabled) {
    background-color: #374151 !important;
}

.dark .editor_container__word-count {
    background-color: #1f2937 !important;
    color: #e5e7eb !important;
    border-color: #4b5563 !important;
}

.dark img, .dark figure, .dark video {
    filter: brightness(0.8) contrast(1.2);
}

.dark .ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-sticky-panel__content {
    border-color: #4b5563 !important;
}

.dark .ck .ck-toolbar__separator {
    background-color: #4b5563 !important;
}

.dark .ck.ck-button.ck-on, a.ck.ck-button.ck-on {
    background-color: #4b5563 !important;
    color: #e5e7eb !important;
}

.dark .ck.ck-list {
    background-color: #1f2937 !important;
    border-color: #4b5563 !important;
}

.dark .ck.ck-button.ck-list-item-button, .ck.ck-button.ck-list-item-button.ck-on {
    background-color: #1f2937 !important;
    color: #e5e7eb !important;
}

.dark .ck.ck-button, a.ck.ck-button {
    background-color: #1f2937 !important;
    color: #e5e7eb !important;
}

.ck.ck-sticky-panel .ck-sticky-panel__content_sticky {
    position: unset;
}

.ck .ck-sticky-panel__placeholder {
    height: 0 !important;
}
</style>
